/* eslint-disable react/prop-types */
import { forwardRef, useContext, useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import shallow from 'zustand/shallow';

import useCanRefresh from './useCanRefresh';

import Button from 'components/Button';
import Typography from 'components/Typography';
import useBaseURL from 'hooks/useBaseURL';
import clsx from 'libs/clsx';
import { safeJoinURL } from 'libs/url';
import AppContext from 'context/app.context';
import { openModalImage } from 'actions/app.action';

function ExamQuestion(
  {
    currentQuestionIndex,
    question,
    questionTotal,
    onRefresh,
    answerComponent,
    examNavigation,
  },
  // eslint-disable-next-line no-unused-vars
  ref
) {
  const path = useBaseURL();
  const [, dispatchApp] = useContext(AppContext);

  const [isRefreshLoading, setIsRefreshLoading] = useState(false);

  // @todo optimasi component yang terender. Terlalu banyak merender.
  const refresh = useCanRefresh((state) => state, shallow);
  const refreshStatus = useCanRefresh(
    (state) => ({
      status: state.list[currentQuestionIndex]?.status,
      willEnabledOn: state.list[currentQuestionIndex]?.willEnabledOn,
      enableBackByTimeout: () =>
        state.enableBackByTimeout(currentQuestionIndex),
    }),
    shallow
  );

  const handleHighlightImage = (url) => {
    dispatchApp(openModalImage(url));
  };

  useEffect(() => {
    if (refresh.list.length === 0 || refresh.list.length !== questionTotal) {
      refresh.initList(questionTotal);
    }
  }, [questionTotal, refresh, refresh.list.length]);

  // Apakah pertanyaan memiliki karakter [img],
  const questionContainImg =
    /\[img\]/i.test(question.question) ||
    /\[img\]/i.test(question.additionalQuestion);

  const isGBRContainMp3 = /\.(?:wav|mp3)$/i.test(question.image);

  // Replace [img] dengan tag img pada html.
  const questionString = question.question
    .split(/\[img\]/i)
    .reduce((acc, cur, index) => {
      if (index === 0) return [cur];

      const imageUrl = safeJoinURL(path.questionImage, question.image);

      return [
        ...acc,
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <img
          className="cursor-pointer"
          src={`${safeJoinURL(path.questionImage, question.image)}`}
          alt={`${question.image}`}
          onClick={() => handleHighlightImage(imageUrl)}
        />,
        cur,
      ];
    }, []);

  const additionalQuestionArray = (question.additionalQuestion || '')
    .split(/\[img\]/i)
    .reduce((acc, cur, index) => {
      if (index === 0) return [cur];

      const imageUrl = safeJoinURL(path.questionImage, question.image);

      return [
        ...acc,
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <img
          className="cursor-pointer"
          src={`${safeJoinURL(path.questionImage, question.image)}`}
          alt={`${question.image}`}
          onClick={() => handleHighlightImage(imageUrl)}
        />,
        cur,
      ];
    }, []);

  // const questionArabic = /[\u0600-\u06FF]/.test(question.question);

  // Refresh soal.
  const handleRefreshClick = () => {
    if (!refreshStatus.status) return;

    setIsRefreshLoading(true);
    onRefresh({ done: () => setIsRefreshLoading(false) });

    refreshStatus.enableBackByTimeout();
  };

  return (
    <>
      <div className="flex-0">{examNavigation}</div>

      <div className="flex flex-0 items-center justify-start gap-5 pt-10 mb-3">
        <Typography variant="subtitle1" className="font-medium">
          Pertanyaan
        </Typography>

        <div>
          <Button
            type="button"
            onClick={handleRefreshClick}
            size="sm"
            color="secondary"
            data-tip={
              refreshStatus.status
                ? 'Refresh data soal'
                : `Anda dapat mencoba lagi dalam ${refreshStatus.willEnabledOn} detik`
            }
            loading={isRefreshLoading}
            disabled={isRefreshLoading}
          >
            Refresh&nbsp;
            {!refreshStatus.status && <>({refreshStatus.willEnabledOn})</>}
          </Button>

          <ReactTooltip />
        </div>
      </div>

      {/* Gambar tambahan */}
      {question.image && !questionContainImg && !isGBRContainMp3 && (
        <div className="mt-5">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
          <img
            className="w-auto max-w-full block cursor-pointer"
            src={safeJoinURL(path.questionImage, question.image)}
            alt={question.image}
            onClick={() =>
              handleHighlightImage(
                safeJoinURL(path.questionImage, question.image)
              )
            }
          />
        </div>
      )}

      <div
        className={clsx(
          'grid shrink-0 overflow-hidden',
          question.additionalQuestion ? 'grid-cols-2 gap-10' : 'grid-cols-1'
        )}
      >
        {/* Paragraf tambahan */}
        {question.additionalQuestion && (
          <div className="overflow-y-auto h-full">
            {additionalQuestionArray.map((item, index) => {
              if (typeof item === 'string') {
                return (
                  <div
                    key={index}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: item,
                    }}
                  />
                );
              }

              return item;
            })}
          </div>
        )}

        <div className="overflow-y-auto h-full">
          {/* Pertanyaan */}
          <Typography
            as="div"
            className={clsx(
              'font-readable select-none',
              !question.additionalQuestion && 'mt-5'
            )}
            gutterBottom
          >
            {questionString.map((item, index) => {
              if (typeof item === 'string') {
                return (
                  <div
                    key={index}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: item,
                    }}
                  />
                );
              }

              return item;
            })}
          </Typography>

          {/* Mp3 tambahan */}
          {question.image && !questionContainImg && isGBRContainMp3 && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              src={safeJoinURL(path.questionImage, question.image)}
              controls
              preload="auto"
              controlsList="nofullscreen nodownload noremoteplayback noplaybackrate"
              className="mx-auto"
            />
          )}

          {answerComponent}
        </div>
      </div>
    </>
  );
}

export default forwardRef(ExamQuestion);
